
import { defineComponent, PropType, reactive, toRaw } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// realisations
import { geocoder } from '@/core/realisations/geocoder.realisation'

// composable
import { useDebounce } from 'vue-composable'

export default defineComponent({
    components: {
        'the-input': TheInput,
        loader: Loader,
    },
    props: {
        twogisGeocoder: {
            type: Boolean,
            default: true,
        },
        yandexGeocoder: {
            type: Boolean,
            default: false,
        },
        cityName: {
            type: String,
            default: null,
        },
        bbox: {
            type: Array as PropType<number[][]>,
            default: null,
        },
    },
    emits: ['select'],
    setup(props, { emit }) {
        const state = reactive({
            is_loading: false,
            is_failed: false,
            geocode_query: null as string | null,
            results: [] as {
                name: string
                street: string | null
                description?: string
                coordinates: number[]
            }[],
        })

        const formatCoordinatesFromString = (coords_string: string): number[] => {
            const coords = coords_string.split(' ')
            const latitude = Number(coords[1])
            const longitude = Number(coords[0])

            return [latitude, longitude]
        }

        const geocodeAddress = async () => {
            if (!state.geocode_query) {
                state.is_loading = false
                state.is_failed = false
                state.results = []
                return
            }

            try {
                state.is_loading = true

                let query = ''

                if (props.cityName) {
                    query = `${props.cityName}, ${state.geocode_query}`
                } else {
                    query = state.geocode_query
                }

                if (props.yandexGeocoder) {
                    const response = await geocoder.yandexGeocode(query, props.bbox)

                    if (response.response.GeoObjectCollection.featureMember.length === 0) {
                        state.results = []
                        return
                    }

                    state.results = response.response.GeoObjectCollection.featureMember.map(
                        (feature_member) => {
                            const address_components =
                                feature_member.GeoObject.metaDataProperty.GeocoderMetaData.Address
                                    .Components
                            const object_address_components: Record<string, string> = {}

                            address_components.forEach((component) => {
                                object_address_components[component.kind] = component.name
                            })

                            return {
                                name: feature_member.GeoObject.name,
                                street: object_address_components.street || null,
                                description: feature_member.GeoObject.description,
                                coordinates: formatCoordinatesFromString(
                                    feature_member.GeoObject.Point.pos
                                ),
                            }
                        }
                    )
                } else {
                    const response = await geocoder.twoGisGeocodeByAddress(query, props.bbox)

                    if (!response.result) {
                        state.results = []
                        return
                    }

                    state.results = response.result.items.map((item) => {
                        const coordinates = [item.point.lat, item.point.lon]

                        let country = undefined as string | undefined
                        let city = undefined as string | undefined
                        let hint = undefined as string | undefined

                        if (item.adm_div) {
                            item.adm_div.forEach((adm_div) => {
                                if (adm_div.type === 'country') country = adm_div.name
                                if (adm_div.type === 'city') city = adm_div.name
                            })

                            if (!country) country = item.adm_div[0].name
                            if (!city) city = item.adm_div[item.adm_div.length - 1].name

                            hint = `${country}, ${city}`
                        } else {
                            hint = item.name
                        }

                        let street = null as string | null
                        if (item.address_name) {
                            const parts = item.address_name.split(',')
                            street = parts[0]
                        }

                        return {
                            name: item.full_name,
                            street,
                            description: hint,
                            coordinates,
                        }
                    })
                }

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const geocode = useDebounce(geocodeAddress, 1000)

        const emitSelect = (item: {
            name: string
            street: string | null
            description?: string
            coordinates: number[]
        }) => {
            emit('select', toRaw(item))

            state.geocode_query = null
            state.results = []
        }

        return { state, geocode, emitSelect }
    },
})
